import { useEffect, useState } from 'react';
import { ExternalLink, FileText, Loader2 } from 'lucide-react';
import { fetchSecureAssetBlob } from '@/client/api/ownership-claims/secure-asset';
import type { ClaimAttachment } from '@/client/types/ownership-claims';

const isImage = (a: ClaimAttachment) =>
  (a.mime_type ?? '').startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(a.filename ?? '');

/** One piece of claim evidence. Files are encrypted, so they load as an authenticated blob. */
const ClaimAttachmentPreview = ({ attachment }: { attachment: ClaimAttachment }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    fetchSecureAssetBlob(attachment.uuid)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((e: Error) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.uuid]);

  const name = attachment.filename || `File #${attachment.asid}`;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex h-40 items-center justify-center bg-gray-50">
        {error ? (
          <p className="px-3 text-center text-xs text-red-600">{error}</p>
        ) : !url ? (
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        ) : isImage(attachment) ? (
          <img src={url} alt={name} className="h-full w-full object-contain" />
        ) : (
          <FileText className="h-10 w-10 text-gray-400" />
        )}
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-3 py-2">
        <span className="truncate text-xs text-gray-700" title={name}>{name}</span>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
          >
            Open <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
};

export default ClaimAttachmentPreview;
